import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter,
} from '@/components/ui/Dialog';
import Button from '@/components/ui/Button';
import { DataTypeKey } from '../types';
import { SourceChoice } from './SourceSelect';

interface SourceChangeDialogProps {
  open: boolean;
  dataType: DataTypeKey | null;
  choice: SourceChoice | null;
  sourceName?: string;
  /** True when the new source brings a different field vocabulary, so mapping resets. */
  resetsMapping: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const SourceChangeDialog: React.FC<SourceChangeDialogProps> = ({
  open, dataType, choice, sourceName, resetsMapping, onCancel, onConfirm,
}) => {
  const { t } = useTranslation();
  if (!dataType || !choice) return null;

  const dataTypeLabel = t(`extInt.dataType.${dataType}`);

  const body = choice.kind === 'notTracked'
    ? t('extInt.sourceChange.notTrackedBody', { dataType: dataTypeLabel })
    : choice.kind === 'mowazaf'
      ? t('extInt.sourceChange.nativeBody', { dataType: dataTypeLabel })
      : t('extInt.sourceChange.body', { dataType: dataTypeLabel, source: sourceName ?? '' });

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent className="max-w-lg" data-testid="source-change-dialog">
        <DialogHeader>
          <DialogTitle>{t('extInt.sourceChange.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-muted-foreground">{body}</p>
          {resetsMapping && (
            <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/25">
              <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-700 dark:text-amber-300" aria-hidden="true" />
              <p className="text-sm text-amber-800 dark:text-amber-200">
                {t('extInt.sourceChange.mappingReset')}
              </p>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="source-change-cancel">
            {t('common.cancel')}
          </Button>
          <Button onClick={onConfirm} data-testid="source-change-confirm">
            {t('extInt.sourceChange.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SourceChangeDialog;
